import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Modal,
  Box,
  Typography,
  TextareaAutosize,
  MenuItem,
  Select,
  FormControlLabel,
  Button,
  Stack,
  TextField,
  CircularProgress
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ChipInput from './Chip';
import IOSSwitch from './IOSSwitch';
import { fetchAllMembers } from '../../pages/HomePage/service';
import { useDispatch, useSelector } from 'react-redux';
import { updateTaskRx, updateComprehensiveTaskRx } from '../../redux/Calendar/calendarSlice';
import { calculateWeeksbyDate, parseDateByDash } from '../../utils/Utils';
import { useOkta } from '../../hooks/oktaHook';
import { updateTaskDetails } from '../../pages/Calendar/service';
import { startOfToday } from 'date-fns';

const CalendarModal = ({ open, handleClose, selectedTaskId, screenId, fetchApi }) => {
  const initialTaskState = {
    taskId: '',
    startDate: null,
    endDate: null,
    taskTitle: '',
    taskDescription: '',
    assignee: [],
    outlook: false,
    periodWeeks: 0,
    status: 'Not Started'
  };
  const [taskData, setTaskData] = useState(initialTaskState);
  const dispatch = useDispatch();
  const { userDetails } = useOkta();
  const { email } = userDetails;
  const { search } = useLocation();
  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedBu = newParams.get('bu');
  const updatedTemplateDataRx = useSelector((state) => state.calendar.updatedData); // template screen handle
  const comprehensiveTaskDataRx = useSelector((state) => state.calendar.comprehensiveData); // comprehensive screen handle
  const [team, setTeam] = useState([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const data = screenId === 'template' ? updatedTemplateDataRx : comprehensiveTaskDataRx;
  const memoizedSelectedBu = useMemo(() => selectedBu, [selectedBu]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchAllMembers(memoizedSelectedBu, memoizedSelectedBu);
        const leads = response?.listOfLead?.map((v) => v?.userEmail) || [];
        const members = response?.listOfMember?.map((v) => v?.userEmail) || [];
        setTeam([...leads, ...members]);
      } catch (error) {
        console.error('Error fetching team members:', error);
      }
    };

    if (open) {
      fetchData();
    }
  }, [memoizedSelectedBu, open]);

  // Existing code for parsing dates...
  useEffect(() => {
    if (open && data && selectedTaskId) {
      const selectedTask = Array.isArray(data)
        ? data.find((item) => item.taskId === selectedTaskId)
        : null;
      if (selectedTask) {
        setTaskData({
          taskId: selectedTask.taskId || '',
          startDate: selectedTask.timeDuration?.start
            ? parseDateByDash(selectedTask.timeDuration.start)
            : null,
          endDate: selectedTask.timeDuration?.end
            ? parseDateByDash(selectedTask.timeDuration.end)
            : null,
          taskTitle: selectedTask.name || '',
          taskDescription: selectedTask.taskDescription || '',
          assignee: selectedTask?.assignee || [],
          outlook: selectedTask?.outlook,
          periodWeeks: selectedTask?.periodWeeks,
          status: selectedTask?.status //added status attribute
        });
      }
    }
  }, [open, data, selectedTaskId]);

  // Format date to DD-MM-YYYY
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`; // Returns date in DD-MM-YYYY format
  };

  const updateCompreensiveTask = useCallback(
    async (data) => {
      if (!data) return;
      if (memoizedSelectedBu && email) {
        const payload = {
          buName: memoizedSelectedBu,
          userName: email,
          tasksList: [data],
          cycleId: selectedCycleId
        };
        try {
          const response = await updateTaskDetails(payload);
          return response;
        } catch (error) {
          console.error('Error fetching updateTask data:', error);
        }
      }
    },
    [memoizedSelectedBu, email]
  );

  const handleSave = async () => {
    const updatedtask = {
      taskId: taskData.taskId,
      timeDuration: {
        start: formatDate(taskData.startDate),
        end: formatDate(taskData.endDate)
      },
      name: taskData.taskTitle,
      taskDescription: taskData.taskDescription,
      assignee: taskData.assignee,
      outlook: taskData.outlook,
      periodWeeks: taskData.periodWeeks,
      status: taskData?.status
    };
    //update redux state based on screen id and api responses
    if (screenId === 'comprehensive') {
      setUpdateLoading(true);
      const response = await updateCompreensiveTask(updatedtask);
      if (response === 'success') {
        // if saved to db then only update redux
        dispatch(updateComprehensiveTaskRx(updatedtask));
        setUpdateLoading(false);
      }
    }
    dispatch(updateTaskRx(updatedtask)); //for template screen , so api call on save
    handleClose('submit'); // Close the modal after saving
  };
  const handleAssigneeChange = (newAssignees) => {
    setTaskData((prev) => ({ ...prev, assignee: newAssignees }));
  };

  // Filter team members to exclude already-selected assignees
  const availableTeamMembers = useMemo(
    () => team.filter((member) => !taskData.assignee.includes(member)),
    [team, taskData.assignee]
  );

  const handleDescriptionChange = (e) => {
    setTaskData((prev) => ({ ...prev, taskDescription: e.target.value }));
  };

  const handleOutlookToggle = (e) => {
    setTaskData((prev) => ({ ...prev, outlook: e.target.checked }));
  };
  const handleStartDateChange = (newValue) => {
    const calculatedWeeks = calculateWeeksbyDate(newValue, taskData?.endDate);
    setTaskData((prev) => ({ ...prev, startDate: newValue, periodWeeks: calculatedWeeks }));
  };

  const handleEndDateChange = (newValue) => {
    // calculates week diff by start and end date
    const calculatedWeeks = calculateWeeksbyDate(taskData?.startDate, newValue);
    setTaskData((prev) => ({ ...prev, endDate: newValue, periodWeeks: calculatedWeeks }));
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '40px',
            gap: '16px',
            position: 'absolute',
            width: '561px',
            height: '648px',
            left: 'calc(50% - 561px/2 + 10.5px)',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#FBFCFE',
            border: '10px solid #D7E5F8',
            boxShadow: '0px 2px 24px 8px rgba(0, 92, 188, 0.12)',
            borderRadius: '8px',
            maxHeight: '90vh',
            overflowY: 'auto',
            scrollbarWidth: 'thin', // For Firefox
            scrollbarColor: 'rgba(0,0,0,0.2) transparent', // For Firefox
            '&::WebkitScrollbar': {
              width: '6px'
            },
            '&::WebkitScrollbarTrack': {
              background: 'transparent'
            },
            '&::WebkitScrollbarThumb': {
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '3px'
            }
          }}>
          <Typography variant="h6" sx={{ alignSelf: 'flex-start' }}>
            E2E Task Information
          </Typography>

          <Stack spacing={2} sx={{ width: '100%' }}>
            <Typography variant="h10">Task Title</Typography>
            <Select
              value={taskData.taskTitle}
              disabled
              displayEmpty
              sx={{
                width: '100%',
                height: '32px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px',
                padding: '8px',
                color: '#333'
              }}>
              <MenuItem value={taskData.taskTitle}>{taskData.taskTitle}</MenuItem>
            </Select>

            <Typography variant="h10">Timeline</Typography>
            <Box fullwidth>
              <Stack direction="row" spacing={2}>
                <DatePicker
                  data-testid="start-date"
                  format="MM/dd/yyyy"
                  label="Start Date"
                  value={taskData?.startDate}
                  onChange={handleStartDateChange}
                  renderInput={(params) => <TextField {...params} />}
                  sx={{ width: '100%' }}
                  error={
                    taskData?.startDate &&
                    taskData?.endDate &&
                    taskData?.startDate > taskData?.endDate
                  }
                  helperText={
                    taskData?.startDate &&
                    taskData?.endDate &&
                    taskData?.startDate > taskData?.endDate
                      ? 'Start Date must be before End Date'
                      : ''
                  }
                  minDate={startOfToday()}
                />
                <DatePicker
                  data-testid="end-date"
                  format="MM/dd/yyyy"
                  label="End Date"
                  value={taskData.endDate}
                  onChange={handleEndDateChange}
                  minDate={taskData.startDate || startOfToday()}
                  renderInput={(params) => <TextField {...params} />}
                  sx={{ width: '100%' }}
                  error={
                    taskData.startDate && taskData.endDate && taskData.startDate > taskData.endDate
                  }
                  helperText={
                    taskData.startDate && taskData.endDate && taskData.startDate > taskData.endDate
                      ? 'End Date must be after Start Date'
                      : ''
                  }
                />
              </Stack>
            </Box>

            <Typography variant="h10" sx={{ marginTop: '24px' }}>
              Assign
            </Typography>
            <ChipInput
              value={taskData.assignee}
              onChange={handleAssigneeChange}
              teamMembers={availableTeamMembers}
            />

            <Typography variant="h10">Notes</Typography>
            <TextareaAutosize
              id="description"
              data-testid="step-description"
              placeholder="Type here"
              value={taskData?.taskDescription}
              onChange={handleDescriptionChange}
              rows={4}
              style={{
                width: '100%',
                minHeight: '124px',
                resize: 'both',
                boxSizing: 'border-box',
                borderColor: 'rgba(192, 192, 192, 1)'
              }}
            />

            <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
              <Typography variant="h10">Outlook Notifications</Typography>
              <FormControlLabel
                control={<IOSSwitch checked={taskData?.outlook} onChange={handleOutlookToggle} />}
                label={taskData?.outlook ? 'Yes' : 'No'}
                labelPlacement="end"
                sx={{ marginLeft: '4px', gap: '2px' }}
              />
            </Stack>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                marginRight: '8px'
              }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleClose('cancel')}
                sx={{ marginRight: '16px' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                endIcon={updateLoading && <CircularProgress size={20} color="inherit" />}
                disabled={
                  !taskData.startDate || !taskData.endDate || taskData.startDate > taskData.endDate
                }>
                Save
              </Button>
            </Box>
          </Stack>
        </Box>
      </LocalizationProvider>
    </Modal>
  );
};

export default CalendarModal;
